export type PositionTypeString =
  | "DDH_DDE"
  | "DDE"
  | "DDE_SED"
  | "SED"
  | "DDH"
  | "DLE"
  | "DLD"
  | "DV"
  | "DLE_DV"
  | "DDH_DLE"
  | "DDH_DLD"
  | "DLD_DV"
  | "FALL_RAISED"
  | "UPSIDE_DOWN"
  | "UNK"
  | "INV";

export const PositionType = {
  DDH_DDE: "DDH_DDE",
  DDE: "DDE",
  DDE_SED: "DDE_SED",
  SED: "SED",

  DDH: "DDH",
  DLE: "DLE",
  DLD: "DLD",
  DV: "DV",

  DLE_DV: "DLE_DV",
  DDH_DLE: "DDH_DLE",
  DDH_DLD: "DDH_DLD",
  DLD_DV: "DLD_DV",

  FALL_RAISED: "FALL_RAISED",
  UPSIDE_DOWN: "UPSIDE_DOWN",
  UNK: "UNK",
  INV: "INV",
} as const;

export type PositionTypes = typeof PositionType[keyof typeof PositionType];

export interface PositionMatcher {
  id: PositionTypes;
  isMatch: (x: number, y: number) => boolean;
  linked?: Array<PositionTypes>;
}

// Functions to determine the color of a decubitus position
export function getDecubitusPositionColor(
  position: PositionTypeString
): string {
  switch (position) {
    case PositionType.DDH:
      return "#ffe0b2";
    case PositionType.DLE:
      return "#c5cae9";
    case PositionType.DLD:
      return "#e1bee7";
    default:
      return "transparent";
  }
}
export function getDecubitusPositionColorDarker(
  position: PositionTypeString
): string {
  switch (position) {
    case PositionType.DDH:
      return "#e5a23fff";
    case PositionType.DLE:
      return "#7986cb";
    case PositionType.DLD:
      return "#ba68c8";
    default:
      return "transparent";
  }
}

// Function to determine, given the current date, what is the decubitus position in the FIXED clock approach
export const getFixedClockDecubitusPositionForDate = (
  currentDate: Date,
  hoursInterval: number = 2
): PositionTypeString => {
  // The order of positions
  const positions: PositionTypeString[] = [
    PositionType.DLD,
    PositionType.DDH,
    PositionType.DLE,
    PositionType.DLD,
    PositionType.DDH,
    PositionType.DLE,
  ];

  // Calculate the total number of intervals in a 24-hour day
  const intervalsPerDay = 24 / hoursInterval;

  // Calculate the current interval index
  const hours = currentDate.getHours();
  const minutes = currentDate.getMinutes();
  const totalMinutes = hours * 60 + minutes;
  const intervalIndex =
    Math.floor(totalMinutes / (hoursInterval * 60)) % intervalsPerDay;

  // Determine the position based on the interval index
  return positions[intervalIndex % positions.length];
};

// Function to determine the next decubitus position time in the FIXED clock approach
export const getFixedClockDecubitusNextPositionTimeForDate = (
  currentDate: Date,
  hoursInterval: number = 2
): Date => {
  // Get current time in minutes since midnight
  const hours = currentDate.getHours();
  const minutes = currentDate.getMinutes();
  const totalMinutes = hours * 60 + minutes;

  // Calculate the start of the current interval in minutes
  const intervalStartMinutes =
    Math.floor(totalMinutes / (hoursInterval * 60)) * hoursInterval * 60;

  // Calculate the start of the next interval in minutes
  const nextIntervalStartMinutes = intervalStartMinutes + hoursInterval * 60;

  // Calculate how many days to add
  const daysToAdd = Math.floor(nextIntervalStartMinutes / (24 * 60));
  const minutesInDay = nextIntervalStartMinutes % (24 * 60);

  // Build a new Date for the next interval
  const nextDate = new Date(currentDate);
  nextDate.setHours(0, 0, 0, 0); // reset to midnight
  nextDate.setDate(nextDate.getDate() + daysToAdd);
  nextDate.setMinutes(minutesInDay);

  return nextDate;
};

// Function to determine, given a start date and the current date, what is the decubitus position in the DYNAMIC clock approach
export const getDynamicClockDecubitusPositionForDate = (
  startDate: Date,
  currentDate: Date,
  hoursInterval: number = 2
): PositionTypeString => {
  const positionInFixedClock = getFixedClockDecubitusPositionForDate(
    startDate,
    hoursInterval
  );

  let positions: PositionTypeString[] = [];
  if (positionInFixedClock === PositionType.DDH) {
    positions = [PositionType.DDH, PositionType.DLE, PositionType.DLD];
  } else if (positionInFixedClock === PositionType.DLD) {
    positions = [PositionType.DLD, PositionType.DDH, PositionType.DLE];
  } else if (positionInFixedClock === PositionType.DLE) {
    positions = [PositionType.DLE, PositionType.DLD, PositionType.DDH];
  }

  // We know that the patient started their clock at the position "positionInFixedClock"
  // The patient will do "hoursInterval" hours in that position, then move to the next position

  // Now, given the current date, we need to determine in which position the patient is
  // For that, for each "hoursInterval" hours passed, we will move to the next position

  const timeDifference = currentDate.getTime() - startDate.getTime();
  const hoursDifference = timeDifference / (1000 * 60 * 60);
  const intervalsPassed = Math.floor(hoursDifference / hoursInterval);
  const positionIndex = intervalsPassed % positions.length;

  return positions[positionIndex];
};

// Function to determine the next decubitus position time in the DYNAMIC clock approach
export const getDynamicClockDecubitusNextPositionTimeForDate = (
  startDate: Date,
  currentDate: Date,
  hoursInterval: number = 2
): Date => {
  // Calculate how many intervals have passed since startDate
  const timeDifference = currentDate.getTime() - startDate.getTime();
  const hoursDifference = timeDifference / (1000 * 60 * 60);
  const intervalsPassed = Math.floor(hoursDifference / hoursInterval);

  // The next change is at startDate + (intervalsPassed + 1) * hoursInterval
  const nextChangeMs =
    startDate.getTime() +
    (intervalsPassed + 1) * hoursInterval * 60 * 60 * 1000;
  return new Date(nextChangeMs);
};
